import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Tabs } from 'equasion-lib';
import { FiCameraOff, FiZap } from 'react-icons/fi';
import Background from '@/lib/images/login-bg.svg';
import ScanModeSwitch, { LaserModePanel } from '@/lib/shared-coms/ScanModeSwitch';
import { SCAN_MODE } from '@/lib/scanning/scanMode';
import {
  useScanner,
  SCAN_PROFILE
} from '../../../../root/App/com/ScannerProvider/ScannerProvider';
import './SharedScanner.scss';

/**
 * Process a raw SSCC input (manual entry) to extract the 17 meaningful digits.
 * Handles 20-digit (with AI 00), 18-digit (with check digit), or 17-digit input.
 */
const processSSCCInput = (value: string): string | null => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 20 && digits.startsWith('00')) {
    return digits.slice(2, 19);
  }
  if (digits.length === 18) {
    return digits.slice(0, 17);
  }
  if (digits.length === 17) {
    return digits;
  }
  return null;
};

const SharedScanner = ({
  handleScan,
  hasManualEntry = true,
  handleManualEntry,
  alternativeManualEntry,
  alternativeManualEntryTitle = 'Unserialized',
  showSerial = true,
  showSSCC = false,
  scanLot,
  replaceManualEntry = false,
  // Bump this to pop the entry form open from the page — used when a page-level scan has
  // already worked out what the operator meant and just needs the form in front of them.
  openEntryToken = 0,
  // Notified whenever the entry form opens or closes. A page whose camera stays live behind
  // the form needs this to stop acting on scans that land while the operator is typing.
  onEntryOpenChange,
  // Which decoder settings this screen needs. Pages whose camera reads zone rack labels
  // rather than GS1 item barcodes pass SCAN_PROFILE.ZONE.
  scanProfile = SCAN_PROFILE.ITEM,
  // Camera re-arm delay (ms) after each handled scan, for a continuous-scanning screen (Verify)
  // that suppresses a unit still in frame itself. Left unset, the camera re-arms exactly as it
  // always has (clearScanResult's 3 s default) — every existing screen relies on that blind
  // window to avoid double-reading the same pack.
  rearmDelayMs,
  // Mute Scandit's own beep and vibration while this screen owns the camera, for a screen that
  // sounds its own feedback once it has decided what a read is (Verify: a tone, plus Scandit's beep
  // through the provider's emitDecodeFeedback, when a unit is counted; nothing for the pack still
  // in frame). Left unset, every decode beeps as it always has.
  silentDecode = false
  // setScanLot
}) => {
  const scannerRef = useRef();
  const {
    scanResult,
    setScanResult,
    clearScanResult,
    pushScanTarget,
    popScanTarget,
    activeScanTarget,
    hardwareScanner,
    scanMode
  } = useScanner();
  const laserMode = scanMode === SCAN_MODE.LASER;
  // A zone-scan overlay can take the camera off this page while it stays mounted underneath.
  // While that is happening the overlay owns the scan results, so ignore them here — otherwise
  // a zone label would also be fed to this page's item handler.
  const ownsCamera = activeScanTarget === scannerRef;
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSerialNumberValue, setManualSerialNumberValue] = useState('');
  const [manualGtinValue, setManualGtinValue] = useState('');
  const [manualSSCCValue, setManualSSCCValue] = useState('');
  const tabData = [
    ...(showSerial
      ? [
          {
            label: 'Serial',
            key: 'serial'
          }
        ]
      : []),
    ...(showSSCC
      ? [
          {
            label: 'SSCC',
            key: 'sscc'
          }
        ]
      : []),
    ...(alternativeManualEntry
      ? [
          {
            label: alternativeManualEntryTitle,
            key: 'alternative'
          }
        ]
      : [])
  ];
  const [selectedTab, setSelectedTab] = useState(showSerial ? 'serial' : showSSCC ? 'sscc' : 'alternative');

  useEffect(() => {
    if (scanResult && ownsCamera) {
      try {
        handleScan(scanResult);
      } catch (e) {
        console.error('handleScan error:', e);
      }
      if (typeof rearmDelayMs === 'number') clearScanResult(rearmDelayMs);
      else setScanResult(null);
    }
  }, [scanResult, ownsCamera]);

  useEffect(() => {
    if (!!scanLot) {
      setSelectedTab('alternative');
      setManualOpen(true);
    }
  }, [scanLot]);

  useEffect(() => {
    if (openEntryToken) setManualOpen(true);
  }, [openEntryToken]);

  useEffect(() => {
    onEntryOpenChange?.(manualOpen);
  }, [manualOpen]);

  useEffect(() => {
    pushScanTarget(scannerRef, scanProfile, { silentDecode });
    return () => popScanTarget(scannerRef);
  }, [pushScanTarget, popScanTarget, scanProfile, silentDecode]);

  return (
    <div className="shared-scanner">
      {/* Camera mode only. In laser mode nothing has asked for the camera, so a prompt to allow
          it would be telling the operator to fix something that is not broken. */}
      {!laserMode && (
        <div className="no-camera-background main-background">
          <img className="background" src={Background} />
          <div className="no-camera-message">
            <FiCameraOff />
            <br />
            Allow RxERP to use your devices camera.
          </div>
        </div>
      )}

      {/* A positioned box the exact size of the camera preview, so the mode switch and the laser
          panel sit over the viewfinder rather than over the whole page. Deliberately wraps only
          these three: the manual-entry row and the handheld pill below are positioned against
          the page, and re-anchoring them here would move them. */}
      <div className="shared-scanner__stage">
        {/* Stays mounted in both modes: this element is the scan target, and unmounting it would
            drop the camera's claim on this screen and disarm the handheld listener with it. */}
        <div className="scanner" ref={scannerRef} hidden={laserMode}></div>

        {laserMode && <LaserModePanel detected={hardwareScanner?.detected} />}

        <ScanModeSwitch />
      </div>

      {/* Camera mode only: in laser mode the panel already says the scanner is connected, and
          two live indicators saying the same thing is noise. */}
      {!laserMode && !!hardwareScanner?.detected && (
        <div className="shared-scanner__handheld" role="status">
          <FiZap aria-hidden />
          Handheld scanner active
        </div>
      )}

      {hasManualEntry && (
        <div className="modal-buttons">
          <Button
            className="manual-entry-button"
            type="secondary"
            onClick={handleManualEntry ? handleManualEntry : () => setManualOpen(true)}
          >
            Manual Entry
          </Button>
        </div>
      )}

      <Modal
        className="scanner-manual-entry"
        modalOpen={manualOpen}
        setModalOpen={setManualOpen}
      >
        {replaceManualEntry ? (
          alternativeManualEntry
        ) : (
          <>
            {showSSCC && tabData.length > 1 && (
              <Tabs
                onTabChange={(value) => setSelectedTab(value)}
                selectedKey={selectedTab}
                menuOnly
                data={tabData}
              />
            )}

            {selectedTab === 'serial' && (
              <>
                <strong>Enter product or container identifier</strong>
                <Input
                  form={false}
                  label="GTIN / Company Prefix"
                  value={manualGtinValue}
                  onChange={({ value }) => setManualGtinValue(value)}
                />
                <Input
                  form={false}
                  label="Serial Number"
                  value={manualSerialNumberValue}
                  onChange={({ value }) => setManualSerialNumberValue(value)}
                />
                <Button
                  type="primary"
                  fullWidth
                  onClick={() =>
                    handleScan({
                      serialNumber: manualSerialNumberValue,
                      gtin: manualGtinValue,
                      isManualEntry: true
                    })
                  }
                  disabled={!manualSerialNumberValue || !manualGtinValue}
                >
                  Submit
                </Button>
              </>
            )}

            {selectedTab === 'sscc' && (
              <>
                <strong>Enter SSCC number</strong>
                <Input
                  form={false}
                  label="SSCC"
                  value={manualSSCCValue}
                  onChange={({ value }) => setManualSSCCValue(value)}
                />
                <Button
                  type="primary"
                  fullWidth
                  onClick={() => {
                    const processed = processSSCCInput(manualSSCCValue);
                    if (processed) {
                      handleScan({ serialNumber: processed, gtin: null, isManualEntry: true });
                    }
                  }}
                  disabled={!processSSCCInput(manualSSCCValue)}
                >
                  Submit
                </Button>
              </>
            )}

            {selectedTab === 'alternative' &&
              !!alternativeManualEntry &&
              alternativeManualEntry}
          </>
        )}
      </Modal>
    </div>
  );
};

export default SharedScanner;
